import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { preflightService } from '../api/preflightService'
import { WAR_ROOM_QUERY_KEYS } from '../api/queryClient'
import type { PreflightItem } from '../types/session.types'

export interface PreflightSelectData {
  all: PreflightItem[]
  blocking: PreflightItem[]
  byCategory: Record<string, PreflightItem[]>
  allBlockingPassed: boolean
}

export function usePreflight(sessionId: string) {
  return useQuery({
    queryKey: WAR_ROOM_QUERY_KEYS.preflight(sessionId),
    queryFn: () => preflightService.list(sessionId).then((r) => r.data),
    staleTime: 30_000,
    enabled: !!sessionId,
    select: (items: PreflightItem[]): PreflightSelectData => ({
      all: items,
      blocking: items.filter((i) => i.is_blocking),
      byCategory: groupByCategory(items),
      allBlockingPassed: items
        .filter((i) => i.is_blocking)
        .every((i) => i.status === 'passed' || i.status === 'waived'),
    }),
  })
}

export function useCheckPreflightItem(sessionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      itemId,
      status,
      note,
      evidenceUrl,
    }: {
      itemId: string
      status: 'passed' | 'failed' | 'na'
      note?: string
      evidenceUrl?: string
    }) =>
      preflightService.check(sessionId, itemId, {
        status,
        result_note: note,
        evidence_url: evidenceUrl,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WAR_ROOM_QUERY_KEYS.preflight(sessionId) })
    },
  })
}

export function useWaivePreflightItem(sessionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, reason }: { itemId: string; reason: string }) =>
      preflightService.waive(sessionId, itemId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WAR_ROOM_QUERY_KEYS.preflight(sessionId) })
    },
  })
}

function groupByCategory(items: PreflightItem[]): Record<string, PreflightItem[]> {
  return items.reduce<Record<string, PreflightItem[]>>((acc, item) => {
    const key = item.category
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})
}
